import { Clock } from "lucide-react";
import React from "react";
import { CompactTimeSelect } from "@/components/ui/compact-time-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  minuteInterval?: 1 | 15 | 30 | 60;
  variant?: "native" | "select";
  required?: boolean;
}

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  label,
  placeholder = "Select time",
  error,
  minuteInterval = 1,
  variant = "select",
  required = false,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // Validate time format (HH:MM in 24-hour format)
  const isValidTime = (time: string): boolean => {
    const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return regex.test(time);
  };

  const hasError = error || (value && !isValidTime(value));

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <div className="relative">
        {variant === "native" ? (
          <>
            <Input
              type="time"
              value={value}
              onChange={handleInputChange}
              disabled={disabled}
              placeholder={placeholder}
              className={cn(
                "h-9 pr-10 bg-background",
                hasError && "border-destructive focus-visible:ring-destructive"
              )}
              step="60" // 1-minute precision
              required={required}
            />
            <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <CompactTimeSelect
              value={value}
              onChange={onChange}
              disabled={disabled}
              minuteInterval={minuteInterval}
              placeholder={placeholder}
              size="md"
              className={cn(
                hasError &&
                  "[&_[role=combobox]]:border-destructive [&_[role=combobox]]:focus-visible:ring-destructive"
              )}
            />
          </div>
        )}
      </div>

      {hasError && (
        <p className="text-xs text-destructive">{error || "Please enter a valid time (HH:MM)"}</p>
      )}
    </div>
  );
};

// Utility components for common use cases
export const AppointmentTimeInput: React.FC<Omit<TimeInputProps, "minuteInterval" | "variant">> = (
  props
) => <TimeInput {...props} minuteInterval={1} variant="select" />;

export const BusinessHoursTimeInput: React.FC<
  Omit<TimeInputProps, "minuteInterval" | "variant">
> = (props) => <TimeInput {...props} minuteInterval={15} variant="select" />;

export const NativeTimeInput: React.FC<Omit<TimeInputProps, "variant">> = (props) => (
  <TimeInput {...props} variant="native" />
);
